import { QUOTE, useObservableValue, useSubscription, wsScanToMap, type Quote } from '@talos/kyoko';
import { useMemo } from 'react';
import { map, of } from 'rxjs';

import type { RFQSweeperQuote } from '../RFQSweeper';

export interface QuoteSweepingQuoteMarkets {
  /** Representative parent RFQ quote. Used for shared display metadata like Symbol, PricingMode, and OrderQty. */
  representativeQuote: Quote;
  /** Executable quote/market pairs. Each item preserves the owning quote for its first quote market. */
  sweeperQuotes: RFQSweeperQuote[];
}

/**
 * Subscribes to all quote rows for an RFQ correlation ID and shapes them for the sweeper.
 *
 * The sweeper UI needs one representative quote for shared RFQ metadata, but execution
 * needs the exact parent quote for each selectable market so RFQID/QuoteID are correct.
 * This hook keeps both views of the data together and updates as quote rows stream in.
 */
export function useQuoteSweepingQuoteMarkets(correlationID: string | undefined): QuoteSweepingQuoteMarkets | undefined {
  const quoteRequest = useMemo(
    () =>
      correlationID
        ? {
            name: QUOTE,
            tag: 'useQuoteSweepingQuoteMarkets',
            ParentRFQID: correlationID,
          }
        : null,
    [correlationID]
  );

  const { data: quoteDataObservable } = useSubscription<Quote>(quoteRequest, { loadAll: false });

  const quoteSweepingQuoteMarketsObservable = useMemo(() => {
    if (!correlationID) {
      return of(undefined);
    }

    return quoteDataObservable.pipe(
      map(json => ({
        ...json,
        data: json.data.filter(quote => quote.ParentRFQID === correlationID),
      })),
      wsScanToMap({ getUniqueKey: quote => quote.RFQID, newMapEachUpdate: true }),
      map(quotesByRFQID => getQuoteSweepingQuoteMarkets([...quotesByRFQID.values()]))
    );
  }, [correlationID, quoteDataObservable]);

  return useObservableValue(() => quoteSweepingQuoteMarketsObservable, [quoteSweepingQuoteMarketsObservable]);
}

/**
 * Converts streamed quote rows into the RFQ sweeper data shape.
 *
 * Each quote row contributes its first market as the selectable market. If multiple rows
 * report the same market, keep the first one received so the displayed market list stays
 * unique and stable across websocket updates.
 */
function getQuoteSweepingQuoteMarkets(quotes: Quote[]): QuoteSweepingQuoteMarkets | undefined {
  const quote = quotes.at(0);
  if (!quote) {
    return undefined;
  }

  const quotesByMarket = new Map<string, RFQSweeperQuote>();

  for (const quote of quotes) {
    const quoteMarket = quote.Markets.at(0);

    if (quoteMarket && !quotesByMarket.has(quoteMarket.Market)) {
      quotesByMarket.set(quoteMarket.Market, { quote, quoteMarket });
    }
  }

  return {
    representativeQuote: quote,
    sweeperQuotes: [...quotesByMarket.values()],
  };
}
